/**
 * PM Invoice Ledger (agency-facing) — read-only.
 *
 * Route: /property-manager/invoices
 * Access: any active agency user (admin OR staff). Same list, no
 * mutation controls. All actions (resend / mark-paid / CSV) remain
 * admin-only (A135 v2 backlog).
 *
 * Mirrors the visual language of the A135 admin ledger but PM-facing
 * and simpler:
 *   • summary cards (Outstanding / Overdue / Paid)
 *   • filter chips (All / Outstanding / Overdue / Paid)
 *   • invoice list with Download PDF and Open booking links
 *   • per-filter empty state
 *   • mobile-friendly stacked layout below sm breakpoint
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Download, LogOut, Receipt, AlertTriangle, Clock, CheckCircle2,
  DollarSign, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { formatDateAU } from "@/lib/formatDateAU";

const FILTERS = [
  { key: "all",         label: "All" },
  { key: "outstanding", label: "Outstanding" },
  { key: "overdue",     label: "Overdue" },
  { key: "paid",        label: "Paid" },
];

const STATUS_STYLE = {
  unpaid:    { bg: "bg-amber-100",  text: "text-amber-800",  label: "Unpaid" },
  overdue:   { bg: "bg-rose-100",   text: "text-rose-800",   label: "Overdue" },
  paid:      { bg: "bg-emerald-100", text: "text-emerald-800", label: "Paid" },
  cancelled: { bg: "bg-slate-200",  text: "text-slate-600",  label: "Cancelled" },
};

function money(cents) {
  return `$${(cents / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}


function SummaryCard({ testId, label, cents, count, icon: Icon, accent }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-4 sm:p-5 ${accent}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl sm:text-3xl font-bold tabular-nums">
        {money(cents || 0)}
      </div>
      <div className="mt-1 text-xs text-slate-500 tabular-nums">
        {(count || 0)} invoice{count === 1 ? "" : "s"}
      </div>
    </div>
  );
}


export default function PropertyManagerInvoices() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filter = params.get("status") || "all";

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async (statusFilter) => {
    setError(null);
    try {
      const res = await api.get("/partner/invoices", { params: { status: statusFilter } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load invoices");
    }
  };

  useEffect(() => { load(filter); }, [filter]);

  const onLogout = async () => { await logout(); window.location.assign("/"); };

  const setFilter = (key) => {
    if (key === "all") params.delete("status");
    else params.set("status", key);
    setParams(params);
  };

  const totals = data?.totals || {};
  const items = data?.items || [];

  const emptyCopy = useMemo(() => {
    if (!data) return "";
    if (data.totals.invoice_count === 0)
      return "No invoices yet. Once a booking is invoiced under this agency, it will appear here.";
    switch (filter) {
      case "outstanding": return "Nothing outstanding right now.";
      case "overdue":     return "No overdue invoices — nice work.";
      case "paid":        return "No invoices have been paid yet.";
      default:            return "No invoices match this filter.";
    }
  }, [data, filter]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <BrandLogo to="/property-manager" size="sm" />
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:inline">{user?.email}</span>
            <button
              onClick={onLogout}
              data-testid="pm-invoices-logout-btn"
              className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link
          to="/property-manager"
          data-testid="pm-invoices-back-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Managed Properties
        </Link>

        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              <Receipt className="w-3.5 h-3.5" /> Invoices
            </div>
            <h1
              data-testid="pm-invoices-h1"
              className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight"
            >
              Your agency&apos;s invoice ledger
            </h1>
            <p className="mt-2 text-slate-600 text-sm sm:text-base max-w-2xl">
              Every invoice raised against your agency&apos;s bookings, in one place. Download PDFs for your accounts team. Payments and reminders are handled by Aware — reach out if anything looks wrong.
            </p>
          </div>
        </div>

        {error && (
          <div
            data-testid="pm-invoices-error"
            className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-3 flex items-center gap-2 text-sm text-rose-800"
          >
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Summary cards */}
        <section
          data-testid="pm-invoices-summary"
          className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
        >
          <SummaryCard
            testId="pm-invoices-summary-outstanding"
            label="Outstanding"
            cents={totals.outstanding_cents}
            count={totals.outstanding_count}
            icon={DollarSign}
            accent="border-amber-200 bg-amber-50/40 text-amber-900"
          />
          <SummaryCard
            testId="pm-invoices-summary-overdue"
            label="Overdue"
            cents={totals.overdue_cents}
            count={totals.overdue_count}
            icon={AlertTriangle}
            accent="border-rose-200 bg-rose-50/40 text-rose-900"
          />
          <SummaryCard
            testId="pm-invoices-summary-paid"
            label="Paid"
            cents={totals.paid_cents}
            count={totals.paid_count}
            icon={CheckCircle2}
            accent="border-emerald-200 bg-emerald-50/40 text-emerald-900"
          />
        </section>

        {/* Filters */}
        <section
          data-testid="pm-invoices-filters"
          className="mt-6 flex items-center gap-2 flex-wrap"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`pm-invoices-filter-${f.key}`}
              aria-pressed={filter === f.key}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filter === f.key
                  ? "bg-cyan-600 border-cyan-600 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </section>

        {/* Invoice list */}
        <section
          data-testid="pm-invoices-list"
          className="mt-4"
        >
          {data === null && !error && (
            <div className="text-sm text-slate-500">Loading invoices…</div>
          )}
          {data && items.length === 0 && (
            <div
              data-testid="pm-invoices-empty"
              className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 text-center"
            >
              <FileText className="w-6 h-6 mx-auto text-slate-400" />
              <p className="mt-3 text-sm text-slate-600">{emptyCopy}</p>
            </div>
          )}
          {data && items.length > 0 && (
            <div className="space-y-2">
              {items.map((r) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.unpaid;
                return (
                  <div
                    key={r.reference}
                    data-testid={`pm-invoice-row-${r.reference}`}
                    className={`rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] gap-3 sm:items-center ${
                      r.status === "cancelled" ? "opacity-60" : ""
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          data-testid={`pm-invoice-ref-${r.reference}`}
                          className="font-semibold text-sm text-slate-900 tabular-nums"
                        >
                          {r.reference}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${st.bg} ${st.text}`}
                          data-testid={`pm-invoice-status-${r.reference}`}
                        >
                          {st.label}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-600 truncate">
                        {r.test_name || r.test_id}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                        {r.property_address}
                        {r.purchase_order_number && (
                          <> · PO {r.purchase_order_number}</>
                        )}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                        {r.payment_due_at && r.status !== "paid" && r.status !== "cancelled" && (
                          <>Due {formatDateAU(r.payment_due_at)}</>
                        )}
                        {r.paid_at && r.status === "paid" && (
                          <>Paid {formatDateAU(r.paid_at)}</>
                        )}
                        {r.status === "cancelled" && r.archived_at && (
                          <>Cancelled {formatDateAU(r.archived_at)}</>
                        )}
                      </div>
                    </div>
                    <div
                      data-testid={`pm-invoice-amount-${r.reference}`}
                      className="text-sm sm:text-base font-semibold tabular-nums text-slate-900 sm:min-w-[110px] sm:text-right"
                    >
                      ${r.amount.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {r.pdf_url && (
                        <a
                          href={r.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`pm-invoice-pdf-${r.reference}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold bg-white border border-slate-300 text-slate-700 hover:border-cyan-500 hover:text-cyan-700"
                        >
                          <Download className="w-3 h-3" /> PDF
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
